import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, Inbox, MessagesSquare, Mail, Phone, Building2, ChevronDown, Loader2, Trash2 } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { logo } from "../config";

export default function AdminDashboard() {
  const { user, logout, API } = useAuth();
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSession, setOpenSession] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const load = async () => {
      setLoading(true);
      try {
        const [e, s] = await Promise.all([
          axios.get(`${API}/admin/enquiries`),
          axios.get(`${API}/admin/chat-sessions`),
        ]);
        setEnquiries(e.data.enquiries);
        setSessions(s.data.sessions);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, API]);

  if (user === null) return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  const deleteEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`);
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
    } catch { toast.error("Could not delete enquiry"); }
  };

  const clearEnquiries = async () => {
    if (!window.confirm(`Clear ALL ${enquiries.length} enquiries? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/enquiries`);
      setEnquiries([]);
      toast.success("All enquiries cleared");
    } catch { toast.error("Could not clear enquiries"); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this chat session? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/chat-sessions/${sid}`);
      setSessions((list) => list.filter((s) => s.session_id !== sid));
      toast.success("Chat session deleted");
    } catch { toast.error("Could not delete session"); }
  };

  const clearSessions = async () => {
    if (!window.confirm(`Clear ALL ${sessions.length} chat sessions? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/chat-sessions`);
      setSessions([]);
      toast.success("All chat sessions cleared");
    } catch { toast.error("Could not clear sessions"); }
  };

  return (
    <div className="min-h-screen grain bg-ink text-white">
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="Arco Orbis2" className="h-10 w-10 object-contain" />
            <span className="font-display font-medium tracking-[0.2em] text-sm">ADMIN <span className="gold-text">DASHBOARD</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm font-body text-silver/60 hidden sm:block">{user.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-body text-silver hover:border-gold/50 hover:text-gold transition-colors" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-display font-light text-4xl mb-2">Welcome, <span className="gold-text">{user.name}</span></h1>
        <p className="text-silver/60 font-body text-sm mb-8">Review contact enquiries and Risk Advisor conversations.</p>

        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex gap-2">
            <TabBtn active={tab === "enquiries"} onClick={() => setTab("enquiries")} icon={Inbox} label={`Enquiries (${enquiries.length})`} testid="tab-enquiries" />
            <TabBtn active={tab === "chats"} onClick={() => setTab("chats")} icon={MessagesSquare} label={`Chat Sessions (${sessions.length})`} testid="tab-chats" />
          </div>
          {tab === "enquiries" && enquiries.length > 0 && (
            <button onClick={clearEnquiries} className="inline-flex items-center gap-2 rounded-full border border-red-500/30 px-4 py-2 text-xs font-body text-red-300 hover:bg-red-500/10 transition-colors" data-testid="clear-enquiries">
              <Trash2 size={14} /> Clear All
            </button>
          )}
          {tab === "chats" && sessions.length > 0 && (
            <button onClick={clearSessions} className="inline-flex items-center gap-2 rounded-full border border-red-500/30 px-4 py-2 text-xs font-body text-red-300 hover:bg-red-500/10 transition-colors" data-testid="clear-chats">
              <Trash2 size={14} /> Clear All
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gold" /></div>
        ) : tab === "enquiries" ? (
          <div className="grid md:grid-cols-2 gap-4" data-testid="enquiries-list">
            {enquiries.length === 0 && <p className="text-silver/40 font-body text-sm">No enquiries yet.</p>}
            {enquiries.map((e) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="hairline rounded-xl bg-ink-surface/60 p-5" data-testid="enquiry-card">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display font-light text-lg">{e.name}</h3>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[0.65rem] text-silver/40 font-body">{fmt(e.created_at)}</span>
                    <button onClick={() => deleteEnquiry(e.id)} className="h-7 w-7 rounded-full border border-white/10 flex items-center justify-center text-silver/50 hover:text-red-400 hover:border-red-500/40 transition-colors" title="Delete enquiry" data-testid={`delete-enquiry-${e.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <span className="inline-block mt-1 rounded-full bg-gold/10 border border-gold/20 px-3 py-0.5 text-xs text-gold-light font-body">{e.service}</span>
                <p className="mt-3 text-sm text-silver/70 font-body font-light leading-relaxed">{e.message}</p>
                <div className="mt-4 pt-3 border-t border-white/10 space-y-1.5 text-xs font-body text-silver/60">
                  {e.organisation && <div className="flex items-center gap-2"><Building2 size={13} className="text-gold/60" /> {e.organisation}</div>}
                  <a href={`mailto:${e.email}`} className="flex items-center gap-2 hover:text-gold"><Mail size={13} className="text-gold/60" /> {e.email}</a>
                  {e.telephone && <a href={`tel:${e.telephone}`} className="flex items-center gap-2 hover:text-gold"><Phone size={13} className="text-gold/60" /> {e.telephone}</a>}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="chats-list">
            {sessions.length === 0 && <p className="text-silver/40 font-body text-sm">No chat sessions yet.</p>}
            {sessions.map((s) => (
              <div key={s.session_id} className="hairline rounded-xl bg-ink-surface/60 overflow-hidden" data-testid="chat-session">
                <div className="flex items-center">
                  <button onClick={() => setOpenSession(openSession === s.session_id ? null : s.session_id)} className="flex-1 flex items-center justify-between p-5 text-left">
                    <div>
                      <span className="font-body text-sm text-white">Session {s.session_id.slice(0, 12)}…</span>
                      <span className="ml-3 text-xs text-silver/40">{s.count} messages · {fmt(s.last)}</span>
                    </div>
                    <ChevronDown size={18} className={`text-gold transition-transform ${openSession === s.session_id ? "rotate-180" : ""}`} />
                  </button>
                  <button onClick={() => deleteSession(s.session_id)} className="mr-4 h-8 w-8 rounded-full border border-white/10 flex items-center justify-center text-silver/50 hover:text-red-400 hover:border-red-500/40 transition-colors shrink-0" title="Delete session" data-testid={`delete-session-${s.session_id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
                {openSession === s.session_id && (
                  <div className="px-5 pb-5 space-y-2">
                    {s.messages.map((m, i) => (
                      <div key={i} className={`text-sm font-body font-light rounded-lg px-3 py-2 ${m.role === "user" ? "bg-gold/10 text-gold-light" : "bg-ink text-silver/80"}`}>
                        <span className="text-[0.6rem] uppercase tracking-wider opacity-50 block mb-0.5">{m.role}</span>
                        {m.content}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-body transition-colors ${active ? "bg-gold text-black" : "border border-white/15 text-silver/70 hover:text-gold"}`}>
      <Icon size={16} /> {label}
    </button>
  );
}
